import { projectsProjectFileCategorySchema, projectsProjectFileContentTypeSchema, projectsProjectFileIdSchema, projectsProjectFileNameSchema, } from '@crest/module-projects/contracts/file';
import { platformStorageObjectIdSchema, platformStorageSha256Schema, platformStorageUploadIntentIdSchema, platformStorageUploadTransportSchema, } from '@crest/platform-contracts/storage';
import { z } from 'zod';
export const customerPortalProjectIntakeAssetCapability = 'customer-portal.project-intake-assets.v1';
export const customerPortalProjectIntakeAssetStateSchema = z.enum([
    'pending',
    'authorized',
    'verifying',
    'attached',
    'quarantined',
    'deleting',
    'deleted',
    'transferred',
]);
export const customerPortalProjectIntakeAssetSchema = z.strictObject({
    contractVersion: z.literal(customerPortalProjectIntakeAssetCapability),
    id: z.string().uuid(),
    requestId: z.string().uuid(),
    submissionId: z.string().uuid(),
    originalFileName: projectsProjectFileNameSchema,
    category: projectsProjectFileCategorySchema,
    contentType: projectsProjectFileContentTypeSchema,
    sizeBytes: z.number().int().positive().max(5 * 1_024 * 1_024 * 1_024),
    state: customerPortalProjectIntakeAssetStateSchema,
    capturedAt: z.string().datetime({ offset: true }).nullable(),
    revision: z.number().int().positive(),
    deletedAt: z.string().datetime({ offset: true }).nullable(),
    transferredAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const customerPortalProjectIntakeAssetListResponseSchema = z.strictObject({
    contractVersion: z.literal(customerPortalProjectIntakeAssetCapability),
    data: z.array(customerPortalProjectIntakeAssetSchema).max(200),
});
export const customerPortalPrepareProjectIntakeAssetUploadSchema = z.strictObject({
    commandId: z.string().uuid(),
    originalFileName: projectsProjectFileNameSchema,
    contentType: projectsProjectFileContentTypeSchema,
    sizeBytes: z.number().int().positive().max(5 * 1_024 * 1_024 * 1_024),
    sha256: platformStorageSha256Schema,
});
export const customerPortalProjectIntakeAssetUploadAuthorizationResponseSchema = z.strictObject({
    contractVersion: z.literal(customerPortalProjectIntakeAssetCapability),
    data: z.strictObject({
        asset: customerPortalProjectIntakeAssetSchema,
        transport: platformStorageUploadTransportSchema,
    }),
    idempotent: z.boolean(),
});
export const customerPortalFinalizeProjectIntakeAssetSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
});
export const customerPortalProjectIntakeAssetFinalizationResponseSchema = z.strictObject({
    contractVersion: z.literal(customerPortalProjectIntakeAssetCapability),
    data: customerPortalProjectIntakeAssetSchema,
    accepted: z.boolean(),
    failureCode: z.enum([
        'size_mismatch',
        'content_type_mismatch',
        'hash_mismatch',
        'signature_mismatch',
        'malware_detected',
        'malware_not_cleared',
    ]).nullable(),
    idempotent: z.boolean(),
});
export const customerPortalDeleteProjectIntakeAssetSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
});
export const customerPortalProjectIntakeAssetMutationResponseSchema = z.strictObject({
    contractVersion: z.literal(customerPortalProjectIntakeAssetCapability),
    data: customerPortalProjectIntakeAssetSchema,
    idempotent: z.boolean(),
});
export const customerPortalProjectIntakeTransferAssetSchema = z.strictObject({
    assetId: z.string().uuid(),
    assetRevision: z.number().int().positive(),
    publicLinkId: z.string().uuid(),
    uploadIntentId: platformStorageUploadIntentIdSchema,
    platformObjectId: platformStorageObjectIdSchema,
    originalFileName: projectsProjectFileNameSchema,
    category: projectsProjectFileCategorySchema,
    contentType: projectsProjectFileContentTypeSchema,
    sizeBytes: z.number().int().positive().max(5 * 1_024 * 1_024 * 1_024),
    sha256: platformStorageSha256Schema,
    capturedAt: z.string().datetime({ offset: true }).nullable(),
});
export const customerPortalProjectIntakeTransferAssetsSchema = z.array(customerPortalProjectIntakeTransferAssetSchema).max(200).superRefine((assets, context) => {
    for (const key of ['assetId', 'uploadIntentId', 'platformObjectId']) {
        if (new Set(assets.map((asset) => asset[key])).size !== assets.length) {
            context.addIssue({ code: 'custom', message: `Transferred intake ${key} values must be unique.` });
        }
    }
});
export const customerPortalProjectIntakeTransferMappingSchema = z.strictObject({
    assetId: z.string().uuid(),
    projectFileId: projectsProjectFileIdSchema,
    category: projectsProjectFileCategorySchema,
});
export const customerPortalProjectIntakeTransferMappingsSchema = z.array(customerPortalProjectIntakeTransferMappingSchema).max(200).superRefine((mappings, context) => {
    if (new Set(mappings.map(({ assetId }) => assetId)).size !== mappings.length) {
        context.addIssue({ code: 'custom', message: 'Transferred intake assets must be unique.' });
    }
    if (new Set(mappings.map(({ projectFileId }) => projectFileId)).size !== mappings.length) {
        context.addIssue({ code: 'custom', message: 'Transferred Project files must be unique.' });
    }
});
//# sourceMappingURL=customer-portal-project-intake-asset.contract.js.map